// Aussehen › Eigene Adresse (h-domain): Die Hörerseite des Radios unter einer
// eigenen Domain. Domain eintragen, DNS beim Anbieter setzen (mit den konkreten
// Werten dieser Installation), „Jetzt prüfen“ – sobald die Domain auf uns zeigt,
// gilt sie, und das HTTPS-Zertifikat folgt beim ersten Aufruf.

import { useEffect, useState, type FormEvent } from "react";
import { domainApi } from "../api";
import { errorText } from "../components/streaming-ui";
import { Meldungen, Schalter, Sym } from "../components/social-ui";
import { Badge, Button, Callout, Card, EmptyState, Page, PageHeader, Skeleton } from "../components/ui";
import { datumText } from "../components/hosting-ui";
import { DomainStatusBadge, dnsEintraege, istHauptdomain, type DnsZeile } from "../components/domain-ui";
import type { DnsErgebnis, DomainListe, RadioDomain } from "../types";

/** Nur den Namen: ohne https://, Pfad und Port – so, wie die API ihn speichert. */
function hostAus(eingabe: string): string {
  return eingabe.trim().toLowerCase().replace(/^[a-z][a-z0-9+.-]*:\/\//, "").split(/[/?#]/)[0]!.replace(/:\d*$/, "").replace(/\.$/, "");
}

export function EigeneAdressePage(): JSX.Element {
  const [daten, setDaten] = useState<DomainListe | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [neu, setNeu] = useState("");
  const [mitWww, setMitWww] = useState(true);
  const [laeuft, setLaeuft] = useState<string | null>(null);

  const laden = (): Promise<void> => domainApi.liste().then(setDaten).catch((err) => setFehler(errorText(err)));
  useEffect(() => { void laden(); }, []);

  const melde = (text: string): void => {
    setHinweis(text);
    setFehler(null);
  };

  const tue = async (schluessel: string, fn: () => Promise<unknown>, text?: string): Promise<boolean> => {
    setLaeuft(schluessel);
    try {
      await fn();
      if (text) melde(text);
      await laden();
      return true;
    } catch (err) {
      setFehler(errorText(err));
      setHinweis(null);
      return false;
    } finally {
      setLaeuft(null);
    }
  };

  const hinzufuegen = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    const host = hostAus(neu);
    if (!host) return;
    const www = mitWww && !host.startsWith("www.");
    if (await tue("neu", () => domainApi.anlegen(host, www), `${host} ist eingetragen. Jetzt den DNS-Eintrag beim Domain-Anbieter setzen und dann prüfen.`)) {
      setNeu("");
    }
  };

  const pruefen = (d: RadioDomain): Promise<boolean> =>
    tue(`pruefen:${d.host}`, async () => {
      const { domain } = await domainApi.pruefen(d.host);
      melde(
        domain.status === "zertifikat_aktiv"
          ? `${domain.host} zeigt auf uns und hat ein gültiges Zertifikat.`
          : domain.status === "zeigt_auf_uns"
            ? `${domain.host} zeigt auf uns. Das Zertifikat ist noch nicht da – in ein, zwei Minuten noch einmal prüfen.`
            : `${domain.host} zeigt noch nicht auf uns. Nach einer DNS-Änderung kann es etwas dauern.`
      );
    });

  const hatHaupt = daten?.domains.some((d) => d.primaer && d.bestaetigt_at);
  const voll = daten ? daten.domains.length >= daten.max : false;

  return (
    <Page className="max-w-4xl">
      <PageHeader
        eyebrow="Aussehen"
        title="Eigene Adresse"
        description="Eure Hörerseite unter eurer eigenen Domain, z. B. radio-eifel.de – mit HTTPS-Zertifikat. Die bisherige Adresse bleibt erreichbar."
      />
      <Meldungen error={fehler} notice={hinweis} onClear={() => setHinweis(null)} />

      {!daten && !fehler && <Skeleton className="h-64" />}

      {daten && (
        <>
          {!daten.erlaubt && (
            <Callout tone="warning" title="In eurem Tarif ist keine eigene Domain enthalten" className="mb-5">
              {daten.domains.length > 0
                ? "Was schon eingetragen ist, bleibt und lässt sich weiter prüfen. Neue Domains gehen erst mit einem größeren Tarif – der Betreiber eurer Installation kann ihn erweitern."
                : "Der Betreiber eurer Installation kann den Tarif erweitern oder die eigene Domain für euch freischalten."}
            </Callout>
          )}

          <Card
            title="Domain hinzufügen"
            description="Nur den Namen, ohne https://. Die Domain muss euch gehören – den DNS-Eintrag setzt ihr beim Domain-Anbieter."
            className="mb-5"
          >
            <form onSubmit={(e) => void hinzufuegen(e)} className="space-y-3">
              <div className="flex flex-col gap-2 sm:flex-row">
                <label className="block min-w-0 flex-1">
                  <span className="sr-only">Domain</span>
                  <span className="relative block">
                    <Sym name="link" className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
                    <input
                      className="input pl-9 font-mono text-sm"
                      value={neu}
                      placeholder="radio-eifel.de"
                      disabled={!daten.erlaubt || voll}
                      onChange={(e) => setNeu(e.target.value)}
                      onBlur={() => setNeu((a) => hostAus(a))}
                      inputMode="url" autoCapitalize="off" autoCorrect="off" spellCheck={false}
                    />
                  </span>
                </label>
                <button type="submit" className="btn btn-primary" disabled={!daten.erlaubt || voll || !hostAus(neu) || laeuft === "neu"}>
                  <Sym name="plus" /> {laeuft === "neu" ? "Wird eingetragen …" : "Hinzufügen"}
                </button>
              </div>
              <label className="flex items-start gap-3">
                <Schalter checked={mitWww} onChange={setMitWww} disabled={!daten.erlaubt || voll || hostAus(neu).startsWith("www.")} label="www-Variante mitnehmen" />
                <span className="min-w-0 text-sm">
                  <span className="block font-medium text-primary">www-Variante mitnehmen</span>
                  <span className="block text-xs text-muted">
                    {hostAus(neu) && !hostAus(neu).startsWith("www.") ? `www.${hostAus(neu)}` : "www.eure-domain.de"} leitet dann auf die Hauptadresse um.
                  </span>
                </span>
              </label>
              {voll && <p className="field-hint">Mehr als {daten.max} Domains gehen nicht.</p>}
            </form>
          </Card>

          {daten.domains.length === 0 ? (
            <Card className="mb-5">
              <EmptyState
                icon={<Sym name="link" className="h-5 w-5" />}
                title="Noch keine eigene Domain"
                description="Bis dahin ist eure Hörerseite unter der gewohnten Adresse erreichbar."
              />
            </Card>
          ) : (
            <div className="mb-5 space-y-4">
              {daten.domains.map((d) => (
                <DomainKarte
                  key={d.host}
                  d={d}
                  ziel={daten.ziel}
                  laeuft={laeuft}
                  mehrere={daten.domains.length > 1}
                  onPruefen={() => void pruefen(d)}
                  onHaupt={() => void tue(`haupt:${d.host}`, () => domainApi.aendern(d.host, { primaer: true }), `${d.host} ist jetzt die Hauptadresse.`)}
                  onWww={(v) => void tue(`www:${d.host}`, () => domainApi.aendern(d.host, { mit_www: v }), v ? `www.${d.host} kommt dazu – bitte auch dafür den DNS-Eintrag setzen und prüfen.` : `www.${d.host} ist entfernt.`)}
                  onEntfernen={() => {
                    if (window.confirm(`${d.host} entfernen? Die Hörerseite ist dann unter dieser Adresse nicht mehr erreichbar.`)) {
                      void tue(`weg:${d.host}`, () => domainApi.entfernen(d.host), `${d.host} ist entfernt.`);
                    }
                  }}
                />
              ))}
            </div>
          )}

          <Card title="So funktioniert es">
            <ol className="list-decimal space-y-2 pl-5 text-sm text-secondary">
              <li><strong className="font-medium text-primary">Domain eintragen.</strong> Sie gilt noch nicht – niemand sieht einen Unterschied.</li>
              <li>
                <strong className="font-medium text-primary">DNS-Eintrag beim Domain-Anbieter setzen</strong> mit den Werten, die bei der Domain stehen.
                Änderungen brauchen oft einige Minuten, manchmal Stunden.
              </li>
              <li><strong className="font-medium text-primary">„Jetzt prüfen“.</strong> Zeigt die Domain auf uns, gilt sie sofort – das HTTPS-Zertifikat folgt beim ersten Aufruf.</li>
            </ol>
            <p className="mt-3 text-xs text-muted">
              {hatHaupt
                ? "Mehrere Domains und die www-Variante leiten auf die Hauptadresse um. Kanalseiten mit eigener Hörerseite haben ihre Adresse unter Kanäle › Hörerseite."
                : "Haben mehrere Domains das OK, leiten alle auf die Hauptadresse um. Kanalseiten mit eigener Hörerseite haben ihre Adresse unter Kanäle › Hörerseite."}
            </p>
          </Card>
        </>
      )}
    </Page>
  );
}

function DomainKarte({ d, ziel, laeuft, mehrere, onPruefen, onHaupt, onWww, onEntfernen }: {
  d: RadioDomain;
  ziel: DomainListe["ziel"];
  laeuft: string | null;
  mehrere: boolean;
  onPruefen: () => void;
  onHaupt: () => void;
  onWww: (v: boolean) => void;
  onEntfernen: () => void;
}): JSX.Element {
  const zeilen = dnsEintraege(d, ziel);
  const offen = d.status === "wartet" || (d.www_host !== null && d.www_status === "wartet");
  const [dnsAuf, setDnsAuf] = useState(offen);
  useEffect(() => { if (offen) setDnsAuf(true); }, [offen]);
  const prueft = laeuft === `pruefen:${d.host}`;

  return (
    <Card
      title={
        <span className="flex min-w-0 flex-wrap items-center gap-2">
          <span className="break-all font-mono text-[0.95rem]">{d.host}</span>
          {d.primaer && <Badge tone="brand">Hauptadresse</Badge>}
        </span>
      }
      actions={<DomainStatusBadge status={d.status} />}
    >
      <div className="space-y-4">
        {d.status === "zeigt_auf_uns" && (
          <Callout tone="info" title="Zeigt auf uns – das Zertifikat fehlt noch">
            Beim ersten Aufruf wird es ausgestellt; das dauert meist unter einer Minute. Danach noch einmal prüfen.
            Läuft die Installation hinter einem eigenen Proxy, richtet der Betreiber das Zertifikat dort ein.
          </Callout>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <Button variant={offen ? "primary" : "secondary"} size="sm" onClick={onPruefen} disabled={prueft}>
            <Sym name="check" /> {prueft ? "Wird geprüft …" : "Jetzt prüfen"}
          </Button>
          {d.status !== "wartet" && (
            <a className="btn btn-ghost btn-sm" href={d.url} target="_blank" rel="noopener noreferrer">
              <Sym name="external" /> Öffnen
            </a>
          )}
          {!d.primaer && mehrere && (
            <Button variant="ghost" size="sm" onClick={onHaupt} disabled={laeuft === `haupt:${d.host}`}>Als Hauptadresse</Button>
          )}
          <Button variant="ghost" size="sm" className="text-red-600 dark:text-red-400" onClick={onEntfernen} disabled={laeuft === `weg:${d.host}`}>
            <Sym name="trash" /> Entfernen
          </Button>
        </div>

        <Pruefbericht e={d.pruefung?.dns ?? null} geprueft={d.geprueft_at} />
        {d.www_host && d.pruefung?.www && <Pruefbericht e={d.pruefung.www} geprueft={null} />}

        {!d.host.startsWith("www.") && (
          <label className="flex items-start gap-3">
            <Schalter checked={d.mit_www} onChange={onWww} disabled={laeuft === `www:${d.host}`} label={`www.${d.host} mitnehmen`} />
            <span className="min-w-0 text-sm">
              <span className="block break-all font-medium text-primary">www.{d.host} mitnehmen</span>
              <span className="block text-xs text-muted">
                {d.mit_www
                  ? d.www_status === "zeigt_auf_uns" ? "Zeigt auf uns und leitet auf die Hauptadresse um." : "Wartet noch auf den DNS-Eintrag."
                  : "Leitet auf die Hauptadresse um, sobald der DNS-Eintrag steht."}
              </span>
            </span>
          </label>
        )}

        <div>
          <button type="button" className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline" onClick={() => setDnsAuf((v) => !v)} aria-expanded={dnsAuf}>
            <Sym name={dnsAuf ? "up" : "down"} className="h-3.5 w-3.5" /> DNS-Einträge beim Domain-Anbieter
          </button>
          {dnsAuf && <DnsAnleitung host={d.host} zeilen={zeilen} ziel={ziel} />}
        </div>
      </div>
    </Card>
  );
}

function Pruefbericht({ e, geprueft }: { e: DnsErgebnis | null; geprueft: string | null }): JSX.Element | null {
  if (!e) return <p className="text-xs text-muted">Noch nicht geprüft.</p>;
  return (
    <div className={`flex items-start gap-2 text-sm ${e.zeigt_auf_uns ? "text-emerald-700 dark:text-emerald-300" : "text-secondary"}`}>
      <Sym name={e.zeigt_auf_uns ? "check" : "alert"} className="mt-0.5 h-4 w-4" />
      <div className="min-w-0">
        <span className="break-words"><span className="font-mono text-xs">{e.host}</span>: {e.meldung}</span>
        {geprueft && <span className="block text-xs text-muted">Zuletzt geprüft {datumText(geprueft, true)}</span>}
      </div>
    </div>
  );
}

function Kopieren({ wert }: { wert: string }): JSX.Element {
  const [ok, setOk] = useState(false);
  return (
    <button
      type="button"
      className="rounded p-1 text-muted hover:text-primary"
      aria-label={`${wert} kopieren`}
      title="Kopieren"
      onClick={() => {
        void navigator.clipboard?.writeText(wert).then(() => {
          setOk(true);
          window.setTimeout(() => setOk(false), 1500);
        });
      }}
    >
      <Sym name={ok ? "check" : "copy"} className="h-3.5 w-3.5" />
    </button>
  );
}

function DnsAnleitung({ host, zeilen, ziel }: { host: string; zeilen: DnsZeile[]; ziel: DomainListe["ziel"] }): JSX.Element {
  return (
    <div className="well mt-2 text-sm">
      {zeilen.length === 0 ? (
        <p className="text-secondary">Die Adresse dieser Installation ist nicht bekannt – bitte den Betreiber fragen, worauf die Domain zeigen soll.</p>
      ) : (
        <>
          <p className="mb-2 text-secondary">
            {istHauptdomain(host)
              ? "Für die Hauptdomain (ohne Vorsilbe) einen A-Eintrag anlegen – und einen AAAA-Eintrag, falls angegeben. Alte A/AAAA-Einträge, die woandershin zeigen, entfernen."
              : "Für diese Unterdomain einen CNAME-Eintrag anlegen. Gibt es dort schon A- oder AAAA-Einträge, diese entfernen."}
          </p>
          <ul className="divide-y divide-default rounded-md border border-default bg-surface">
            {zeilen.map((z, i) => (
              <li key={i} className="grid grid-cols-[4.2rem_minmax(0,1fr)] gap-x-3 gap-y-0.5 px-3 py-2 sm:grid-cols-[4.2rem_8rem_minmax(0,1fr)]">
                <span><Badge tone="neutral" className="font-mono">{z.typ}</Badge></span>
                <span className="min-w-0 font-mono text-xs leading-6 text-secondary sm:text-sm">
                  <span className="text-muted sm:hidden">Name: </span>{z.name}
                </span>
                <span className="col-span-2 flex min-w-0 items-center gap-1 sm:col-span-1">
                  <span className="min-w-0 break-all font-mono text-xs text-primary sm:text-sm">{z.wert}</span>
                  <Kopieren wert={z.wert} />
                </span>
              </li>
            ))}
          </ul>
          {!istHauptdomain(host) && ziel.ips.length > 0 && (
            <p className="mt-2 text-xs text-muted">
              Geht bei eurem Anbieter kein CNAME, tut es auch ein A-Eintrag auf {ziel.ips.filter((ip) => !ip.includes(":")).join(", ") || ziel.ips.join(", ")}.
            </p>
          )}
        </>
      )}
    </div>
  );
}
